import { eq, inArray } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@folio/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT disappear silently ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync's **view** can match
 * **ZERO rows and return "success"**. This is not a hypothesis: it was measured
 * in `space-academy-app` (same canon, `activity` table in the local stack,
 * course `06a131d0`) — the course is created, 1.03 s later the unit is created,
 * and **in between NO `course/updated` op exists**: the title the author had
 * just written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the latest checkpoint**. If the checkpoint does not yet contain the
 * newly created row, that row is **ABSENT for that instant** — and an UPDATE
 * against it writes nothing, generates no op, and **does not fail**.
 *
 * Why the lack of an error proves nothing here: the server write-path
 * deliberately **always returns 2xx** (a 4xx would block the client queue
 * forever). A user write vanishing without a trace is the worst possible
 * failure mode in an office suite: renaming a document or replying to a newly
 * created comment and having the change disappear.
 *
 * `patchRowVerified`/`patchRowsVerified` contract: write, **READ BACK**, and
 * compare. If the value did not land, retry with backoff; if it still has not
 * landed when exhausted, **THROW** — the caller reports it instead of leaving
 * the user looking at a field that represents nothing saved.
 *
 * `waitForRowPresent`/`waitForRowsPresent` cover what is not a simple patch:
 * retrying inside a `db.transaction(...)` is impossible (it would sleep with
 * the write lock held), so they wait for presence BEFORE opening the transaction
 * and write inside without further verification.
 *
 * ── What this module deliberately does NOT cover ───────────────────────────
 * Hard `DELETE`s (`deletePageMutation`, `deleteCommentThreadMutation`,
 * `deleteAttachmentMutation`) are not verified: their natural check would be
 * "the row is gone", and "absent" is precisely the ambiguous state in this
 * window — you cannot distinguish "it was deleted" from "it had not arrived
 * yet". Verifying them would require another signal (the CRUD queue itself),
 * not a read-back. The signal that would reopen this: a delete that truly gets
 * lost (a row reappearing after deletion).
 *
 * `user` DOES go through here (`users/mutations.ts`). Why it CANNOT be excluded
 * (and why that remains true after ThemeProvider's 2026-08-13 seed guard, which
 * closed one historical cause but not the generic window) — see the docblock at
 * the top of `users/mutations.ts`. It is not repeated here to avoid two
 * explanations aging independently.
 */

/**
 * Retry backoff. Total ≈ 6.05 s: more than covers a checkpoint round-trip
 * without leaving a quick interaction hanging forever if the row truly does
 * not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is by
  // primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** Reads several rows by id. Empty if `ids` is empty (without hitting the database). */
async function readRows(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
): Promise<Record<string, unknown>[]> {
  if (ids.length === 0) {
    return [];
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await db.select().from(table).where(inArray(idColumn, ids))) as unknown as Record<
    string,
    unknown
  >[];
}

/** `null` and `undefined` are the same "empty" value when compared with SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, reads back, compares the patch
 * fields, and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing when the backoff is exhausted.
 *
 * This function sets `updated_at` on every attempt (and does not compare it: it
 * changes each pass); `patch` must contain only domain fields.
 *
 * For use ONLY outside a `db.transaction(...)`: it retries with `sleep`, which
 * inside an open transaction would sleep with the write lock held.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Like `patchRowVerified`, adapted to an `UPDATE … WHERE id IN (...)`
 * over SEVERAL rows by primary key (cohort trash/restoration).
 * It lands only when EVERY row in `ids` shows the patch — if a single
 * one is outside the view, it retries the entire batch.
 *
 * This does not exist in the canon (`listto-app`): there no mutation touches
 * more than one row at a time. Here `movePageToTrashMutation`/`restorePageFromTrashMutation`
 * move a page and its entire cohort of subpages with the same timestamp, so
 * the verification unit is the batch, not the individual row.
 */
export async function patchRowsVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const idKey = idColumn.name;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(inArray(idColumn, ids));

    const rows = await readRows(db, table, idColumn, ids);
    const byId = new Map(rows.map((row) => [row[idKey], row]));
    const landed = ids.every((id) => {
      const row = byId.get(id);
      return row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value));
    });
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(ids.join(", "), Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * For mutations that are not a simple patch — a transaction that cannot
 * retry internally (it would sleep with the write lock held) and where
 * operating on an absent row does more damage than losing a field: an UPDATE
 * that matches nothing while a sibling INSERT in the SAME transaction does
 * succeed. It waits OUTSIDE with the same backoff, then writes inside
 * without checking again.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Like `waitForRowPresent`, for the batch case: waits for EVERY row in
 * `ids` to exist before opening the transaction that touches them
 * (`restorePageFromTrashMutation` restores an entire cohort at once).
 */
export async function waitForRowsPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
  options: VerifiedWriteOptions = {},
): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const idKey = idColumn.name;

  for (let attempt = 0; ; attempt++) {
    const rows = await readRows(db, table, idColumn, ids);
    const present = new Set(rows.map((row) => row[idKey]));
    if (ids.every((id) => present.has(id))) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(ids.join(", "), ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
